"use client";

import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { Card, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Link } from "@/components/common/Link";
import { Brain, Target, Award, Handshake } from "lucide-react";
export default function AboutPreview() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.3,
    once: true
  });
  const features = [{
    icon: Brain,
    title: "Beyond Traditional Tutoring",
    description: "We combine expert academic tutoring with confidence and mindset coaching—addressing both knowledge and self‑belief for lasting results."
  }, {
    icon: Target,
    title: "Personalised Learning Plans",
    description: "Every student gets a tailored study plan based on their unique learning style, knowledge gaps, and confidence barriers."
  }, {
    icon: Award,
    title: "UK‑Qualified Expert Tutors",
    description: "All tutors are qualified UK teachers with DBS checks and years of classroom experience who genuinely care about student success."
  }, {
    icon: Handshake,
    title: "Partnership with Parents",
    description: "Regular progress reports and open communication keep you informed every step of the way."
  }];
  return <section className="bg-background py-16 px-6 md:px-12 lg:px-24">
      <div className="mx-auto max-w-7xl">
        {}
        <div className="mb-12 flex flex-col gap-4">
          <h2 className="font-display text-4xl font-bold text-primary leading-tight">Who We Are and Why Choose Us</h2>
          <p className="text-foreground text-xl font-semibold leading-relaxed">Education Family Tutors are a dedicated team of UK-qualified teachers providing expert academic support and confidence coaching. We empower students to master their subjects while developing the mindset and focus needed for lifelong success.</p>
        </div>

        {}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center mb-12">
          {}
          <div className="flex flex-col gap-8">
            <div className="flex flex-col gap-4">
              <h3 className="font-display text-2xl font-semibold text-primary">
                The Complete Learning Solution
              </h3>
              <p className="text-foreground text-base leading-relaxed">
                Most tutoring services focus purely on academic content, leaving students to figure out motivation, focus, and exam technique on their own. We take a different approach. Our programmes integrate expert subject tutoring with proven confidence and mindset coaching, addressing both the "what to learn" and the "how to learn it" that schools often overlook.
              </p>
            </div>

            {}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {features.map((feature, i) => {
              const Icon = feature.icon;
              return <Card key={i} data-index={i} className="bg-muted-light-background border border-border transition-all duration-[var(--duration-medium)] ease-[var(--ease-out-back)] hover:-translate-y-1 hover:shadow-strong">
                    <CardHeader className="gap-3">
                      <div className="flex items-center justify-center w-12 h-12 rounded-lg bg-primary/10">
                        <Icon className="w-6 h-6 text-primary" strokeWidth={2} />
                      </div>
                      <CardTitle className="text-lg">
                        {feature.title}
                      </CardTitle>
                      <CardDescription className="text-sm leading-relaxed">
                        {feature.description}
                      </CardDescription>
                    </CardHeader>
                  </Card>;
            })}
            </div>

            {}
            <div className="flex items-center gap-2">
              <Link to="https://educationfamilytutors.com/services/" className="inline-flex items-center gap-2 text-primary font-semibold hover:text-primary-hover transition-colors duration-[var(--duration-fast)]">
                Learn more
                <span aria-hidden="true">→</span>
              </Link>
            </div>
          </div>

          {}
          <div ref={ref} className="flex items-center justify-center transition-all duration-[var(--duration-medium)] ease-[var(--ease-smooth)]" style={{
          opacity: isVisible ? 1 : 0,
          transform: isVisible ? "translateY(0)" : "translateY(15px)"
        }}>
            <Card className="shadow-medium overflow-hidden hover:scale-105 hover:rotate-2 transition-transform duration-[var(--duration-medium)] ease-[var(--ease-out-back)]">
              <img src="https://educationfamilytutors.com/wp-content/uploads/2026/02/53253d5a-0d07-426a-a9c6-abbdd2b3eefd-scaled-1.webp" alt="Education Family Tutors coaching session" className="w-full h-auto object-cover rounded-xl" />
            </Card>
          </div>
        </div>
      </div>
    </section>;
}